import type { BulkUpdateResult } from "@/features/catalog/services/catalog-api.service";

/**
 * One refusal, DERIVED from what `bulkUpdateProducts` returns.
 *
 * Restating the three fields here would compile and stay compiling: this
 * component reads a subset, so the copy could drift behind a rename and only
 * break at the call site, in a file that is not this one. Indexing the
 * endpoint's own type means the shape has a single owner.
 */
export type BulkSkip = BulkUpdateResult["skipped"][number];

/**
 * Refusals gathered under the reason they share.
 *
 * INSERTION ORDER, not count order. The server returns refusals in the order it
 * walked the products, and sorting by group size would move a reason up or down
 * the list between two runs of the same action -- a change the operator can see
 * and cannot account for, because nothing they did caused it.
 *
 * `Map` is what gives that for free: it iterates in the order keys were first
 * inserted, which here is the order the reasons first appeared.
 */
export function groupSkipsByReason(
	skips: BulkSkip[],
): Array<{ reason: string; skips: BulkSkip[] }> {
	const groups = new Map<string, BulkSkip[]>();
	for (const skip of skips) {
		const existing = groups.get(skip.reason);
		if (existing) {
			existing.push(skip);
		} else {
			groups.set(skip.reason, [skip]);
		}
	}
	return [...groups].map(([reason, grouped]) => ({ reason, skips: grouped }));
}

/**
 * How many refusals can be listed in full before the list is the problem.
 *
 * Set to a count that sits inside the height the result banner already
 * occupies. It counts REFUSALS, not groups: ten groups of one is the same wall
 * of text as one group of ten, and it is the wall being avoided.
 */
export const INLINE_LIMIT = 10;

/**
 * Which products a bulk action would not touch, and why.
 *
 * GROUPED BY REASON, because refusals cluster hard. A bulk publish over a real
 * range fails on a handful of distinct gates and most of the refusals are the
 * same one -- "Needs at least one image" will be most of them -- so a flat list
 * is four hundred lines saying three things, and it pushes the table it is
 * reporting on off the screen.
 *
 * COLLAPSED ONLY ONCE THERE IS TOO MUCH TO READ, past `INLINE_LIMIT` refusals.
 * Volume is the whole problem being solved and three refusals were never it:
 * collapsing those costs three clicks to read what used to be free, which is a
 * regression dressed as an improvement. At or below the limit the groups open
 * themselves and the list reads as it always did, with the reason promoted to a
 * heading. `INLINE_LIMIT` owns where that switches; nothing here restates it.
 *
 * THE NAMES STAY either way, one level down. They are why the endpoint returns
 * a name at all: at 500 products the refused ones are spread over twenty pages
 * and none is on screen, so a name cannot be looked up locally. Collapsing may
 * hide them behind a click; it must never be a reason to stop sending them.
 *
 * Every group gets a `<details>`, and every group in a given render starts the
 * same way. A list where some rows open and others do not makes the operator
 * work out which is which before they can read it.
 */
export function BulkRefusalList({ skips }: { skips: BulkSkip[] }) {
	const groups = groupSkipsByReason(skips);
	const startOpen = skips.length <= INLINE_LIMIT;

	return (
		<ul className="mt-1 space-y-1 text-xs">
			{groups.map((group) => (
				<li key={group.reason}>
					<details open={startOpen}>
						<summary className="cursor-pointer">
							{group.skips.length} product
							{group.skips.length === 1 ? "" : "s"} — {group.reason}
						</summary>
						<ul className="mt-1 list-inside list-disc pl-4">
							{group.skips.map((skip) => (
								<li key={skip.productId} className="font-medium">
									{skip.name}
								</li>
							))}
						</ul>
					</details>
				</li>
			))}
		</ul>
	);
}
